import React, { useState } from 'react';
import {
  MessageCircleQuestion,
  Compass,
  ListOrdered,
  Table2,
  Repeat,
  Languages,
  CheckCircle2,
  PauseCircle,
} from 'lucide-react';
import {
  HOW_TO_USE,
  IMPLICIT_FRAME,
  INQUIRY_STEPS,
  END_STATE_CHECK,
  FOOTER_LINE,
  SINGLE_PASS_TABLE,
  THREE_LAP_TABLE,
  THREE_LAP_REMINDER,
  USAGE_REMINDER,
  LANGUAGE_CLARIFICATION_RULE,
  LANGUAGE_ALTERNATES,
  ALL_QUESTIONS,
} from '../data/inquirySequence';

type View = 'flow' | 'single-pass' | 'three-lap' | 'alternates';

const SectionTitle: React.FC<{ children: React.ReactNode; sub?: string }> = ({ children, sub }) => (
  <div className="mb-6">
    <h2 className="text-2xl font-bold text-charcoal-900">{children}</h2>
    {sub && <p className="text-charcoal-600 mt-1">{sub}</p>}
  </div>
);

const QuestionList: React.FC<{ questions: string[] }> = ({ questions }) => (
  <ul className="space-y-2">
    {questions.map((q) => (
      <li key={q} className="text-sm text-charcoal-800 flex gap-2">
        <span className="text-[var(--text-ter)] flex-shrink-0">•</span>
        <span className="italic">&ldquo;{q}&rdquo;</span>
      </li>
    ))}
  </ul>
);

const ProcessTable: React.FC<{
  rows: { step: string; questions: string[]; note?: string; lap?: string }[];
  showLap?: boolean;
}> = ({ rows, showLap }) => (
  <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-x-auto">
    <table className="w-full text-sm min-w-[640px]">
      <thead>
        <tr className="border-b border-[var(--rule)] text-left">
          {showLap && <th className="p-3 font-semibold text-charcoal-700 w-28">Lap</th>}
          <th className="p-3 font-semibold text-charcoal-700 w-[34%]">Step in the Process</th>
          <th className="p-3 font-semibold text-charcoal-700">Valid Questions for This Step</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={`${row.step}-${i}`} className="border-b border-[var(--rule)] last:border-0 align-top">
            {showLap && (
              <td className="p-3 text-charcoal-600 font-medium whitespace-nowrap">{row.lap}</td>
            )}
            <td className="p-3 font-medium text-charcoal-900">{row.step}</td>
            <td className="p-3 text-charcoal-700">
              {row.note ? (
                <span className="text-charcoal-500 italic">{row.note}</span>
              ) : (
                <ul className="space-y-1.5">
                  {row.questions.map((q) => (
                    <li key={q} className="flex gap-2">
                      <span className="text-[var(--text-ter)] flex-shrink-0">•</span>
                      <span>&ldquo;{q}&rdquo;</span>
                    </li>
                  ))}
                </ul>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StepCard: React.FC<{ step: (typeof INQUIRY_STEPS)[number] }> = ({ step }) => {
  const questions = step.questions ?? (step.question ? [step.question] : []);

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-3">
        <span className="w-8 h-8 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] text-sm font-bold flex items-center justify-center flex-shrink-0">
          {step.number}
        </span>
        <div>
          <h3 className="font-semibold text-charcoal-900">{step.name}</h3>
          {step.subtitle && <p className="text-sm text-charcoal-500">{step.subtitle}</p>}
        </div>
      </div>

      {step.noQuestion ? (
        <div className="mb-4 bg-[var(--surface)] border border-[var(--rule)] p-3">
          {step.number === 8 ? (
            <p className="text-sm text-charcoal-600 flex items-center gap-2">
              <PauseCircle className="h-4 w-4 text-charcoal-400" />
              No question — silence, no interpretation, no framework naming
            </p>
          ) : (
            <p className="text-sm text-charcoal-700 italic">
              &ldquo;{step.capabilityStatement}&rdquo;
            </p>
          )}
          {step.internalReminder && (
            <p className="text-xs text-charcoal-500 mt-2">
              Internal reminder: {step.internalReminder}
            </p>
          )}
        </div>
      ) : (
        <div className="mb-4">
          <QuestionList questions={questions} />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="font-semibold text-charcoal-700 mb-1">Purpose</p>
          <ul className="space-y-1 text-charcoal-600">
            {step.purpose.map((p) => (
              <li key={p} className="flex gap-2">
                <span className="text-[var(--text-ter)]">–</span>
                {p}
              </li>
            ))}
          </ul>
        </div>
        {(step.moveOnWhen || step.notes) && (
          <div>
            {step.moveOnWhen && (
              <>
                <p className="font-semibold text-charcoal-700 mb-1">Move on when</p>
                <ul className="space-y-1 text-charcoal-600 mb-3">
                  {step.moveOnWhen.map((m) => (
                    <li key={m} className="flex gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0 mt-0.5" />
                      {m}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {step.notes?.map((n) => (
              <p key={n} className="text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-2 py-1.5">
                {n}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const VIEW_TABS: { id: View; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'flow', label: 'Full flow', icon: ListOrdered },
  { id: 'single-pass', label: 'Single pass', icon: Table2 },
  { id: 'three-lap', label: 'Three laps', icon: Repeat },
  { id: 'alternates', label: 'Loaded words', icon: Languages },
];

export const InquirySequence: React.FC = () => {
  const [view, setView] = useState<View>('flow');

  return (
    <div className="min-h-screen bg-[var(--surface)] py-8 print:bg-[var(--bg-raised)] print:py-4">
      <div className="max-w-6xl mx-auto px-4 space-y-10">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <MessageCircleQuestion className="h-12 w-12 text-[var(--text-ter)]" />
            <h1 className="text-4xl font-bold text-charcoal-900">Inquiry Sequence</h1>
          </div>
          <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
            The order of questions — and why I ask each one. A coach&apos;s flow for surfacing the
            controlling question before moving to answers.
          </p>
        </div>

        {/* How to use */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 sm:p-8">
          <SectionTitle sub="This is a flow, not a script.">How to use this</SectionTitle>
          <ul className="grid sm:grid-cols-2 gap-2">
            {HOW_TO_USE.map((item) => (
              <li key={item} className="text-sm text-charcoal-700 flex gap-2">
                <span className="text-[var(--text-ter)]">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Implicit frame */}
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6 sm:p-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-[var(--text-sec)] mb-2">
            Step 0 — {IMPLICIT_FRAME.name}
          </p>
          <p className="text-charcoal-800 mb-3">
            <span className="font-medium">Posture:</span> {IMPLICIT_FRAME.posture}
          </p>
          <ul className="space-y-1 mb-3">
            {IMPLICIT_FRAME.purpose.map((p) => (
              <li key={p} className="text-sm text-charcoal-700 flex gap-2">
                <span className="text-[var(--text-sec)]">•</span>
                {p}
              </li>
            ))}
          </ul>
          <p className="text-sm text-charcoal-500 italic">{IMPLICIT_FRAME.reminder}</p>
        </div>

        {/* View tabs */}
        <div className="flex flex-wrap gap-2 print:hidden">
          {VIEW_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              className={`inline-flex items-center gap-2 px-4 py-2  border text-sm font-medium transition ${
                view === id
                  ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] ring-2 ring-purple-200'
                  : 'bg-[var(--bg-raised)] text-charcoal-700 border-[var(--rule)] hover:border-[var(--rule)]'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Full flow */}
        {view === 'flow' && (
          <section className="space-y-4">
            <SectionTitle sub="Steps 1–9. Ask one question at a time. Move on when the purpose is satisfied.">
              The inquiry sequence
            </SectionTitle>
            {INQUIRY_STEPS.map((step) => (
              <StepCard key={step.number} step={step} />
            ))}
          </section>
        )}

        {/* Single-pass table */}
        {view === 'single-pass' && (
          <section>
            <SectionTitle sub="Left column = step. Right column = valid questions (options — you do not ask all of them).">
              Inquiry process: steps &amp; questions
            </SectionTitle>
            <ProcessTable rows={SINGLE_PASS_TABLE} />
            <div className="mt-6 bg-[var(--surface)] border border-[var(--rule)] p-5">
              <p className="font-semibold text-charcoal-800 mb-2">Usage reminder</p>
              <ul className="space-y-1">
                {USAGE_REMINDER.map((item) => (
                  <li key={item} className="text-sm text-charcoal-600 flex gap-2">
                    <span className="text-[var(--text-ter)]">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Three-lap table */}
        {view === 'three-lap' && (
          <section>
            <SectionTitle sub="Lap 1 surfaces context. Lap 2 sharpens. Lap 3 reliably produces the controlling question.">
              Inquiry process — three-lap version
            </SectionTitle>
            <ProcessTable
              rows={THREE_LAP_TABLE.map((r) => ({
                lap: r.lap,
                step: r.step,
                questions: r.questions,
                note: r.note,
              }))}
              showLap
            />
            <p className="mt-4 text-sm text-charcoal-700 italic text-center">{THREE_LAP_REMINDER}</p>
          </section>
        )}

        {/* Language alternates */}
        {view === 'alternates' && (
          <section>
            <SectionTitle sub="Same function as “what does that mean in practice?” — vary tone, cognitive load, and social risk.">
              Language clarification alternates
            </SectionTitle>
            <p className="text-sm text-charcoal-700 mb-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
              {LANGUAGE_CLARIFICATION_RULE}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {LANGUAGE_ALTERNATES.map((group) => (
                <div key={group.title} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
                  <h3 className="font-semibold text-charcoal-900 mb-3">{group.title}</h3>
                  <QuestionList questions={group.alternates} />
                  <p className="text-xs font-semibold text-charcoal-500 uppercase tracking-wide mt-4 mb-1">
                    Use when
                  </p>
                  <ul className="space-y-1">
                    {group.useWhen.map((u) => (
                      <li key={u} className="text-xs text-charcoal-600 flex gap-2">
                        <span className="text-[var(--text-ter)]">–</span>
                        {u}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Quick question list — visible on flow view */}
        {view === 'flow' && (
          <section>
            <SectionTitle sub="All valid questions in order — no labels, no extras.">
              Questions only
            </SectionTitle>
            <ol className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 space-y-3">
              {ALL_QUESTIONS.map((q, i) => (
                <li key={q} className="text-sm text-charcoal-800 flex gap-3">
                  <span className="font-semibold text-[var(--text-ter)] w-5 flex-shrink-0">{i + 1}.</span>
                  <span className="italic">&ldquo;{q}&rdquo;</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* End state */}
        <section className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-3">
            <Compass className="h-6 w-6 text-[var(--text-ter)]" />
            <h2 className="text-xl font-bold text-charcoal-900">End state check</h2>
          </div>
          <p className="text-sm text-charcoal-600 mb-3">You are done when:</p>
          <ul className="space-y-2">
            {END_STATE_CHECK.map((item) => (
              <li key={item} className="text-sm text-charcoal-800 flex gap-2">
                <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Footer */}
        <p className="text-center text-charcoal-600 italic pb-4">{FOOTER_LINE}</p>
      </div>
    </div>
  );
};

export default InquirySequence;
